// PRD 11 §2.2 #7 / §4.1 — Counseling Manual list table.
// Columns: CID · Title · Preview · Grade · Area/Sub-area/Item · Status/Date
// (+ Delete column for CONTENT_LIB:WRITE users; ✕ enabled only for Root /
// CONTENT_LIB:DELETE per PRD §3.7).
//
// Row click opens the edit panel (entire row is the target; the delete button
// stops propagation). Title cell uses link-text styling per PRD §4.1.

import { useTranslation } from 'react-i18next';

import { decodeHtmlEntities } from '@/shared/utils/decode-html-entities';

import type { ConsultationManual } from '../types/counseling-manual.types';
import type { ManualSortKey, SortDir } from '../store/counseling-manual-page.store';
import { GradeBadge } from './GradeBadge';
import { ManualStatusBadge } from './ManualStatusBadge';

interface ManualTableProps {
  rows: ConsultationManual[];
  sortKey: ManualSortKey;
  sortDir: SortDir;
  onSort: (key: ManualSortKey) => void;
  onRowOpen: (row: ConsultationManual) => void;
  canDelete: boolean;
  onDelete: (row: ConsultationManual) => void;
}

const TITLE_MAX = 60;
const PREVIEW_MAX = 100;

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max)}...` : value;
}

function sortIcon(active: boolean, dir: SortDir): string {
  if (!active) return '↕';
  return dir === 'asc' ? '↑' : '↓';
}

interface SortableHeaderProps {
  label: string;
  columnKey: ManualSortKey;
  sortKey: ManualSortKey;
  sortDir: SortDir;
  onSort: (key: ManualSortKey) => void;
  style?: React.CSSProperties;
}

function SortableHeader({ label, columnKey, sortKey, sortDir, onSort, style }: SortableHeaderProps) {
  const active = sortKey === columnKey;
  return (
    <th
      data-sort={columnKey}
      onClick={() => onSort(columnKey)}
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      style={style}
    >
      {label} <span className="sort-icon">{sortIcon(active, sortDir)}</span>
    </th>
  );
}

export function ManualTable({
  rows,
  sortKey,
  sortDir,
  onSort,
  onRowOpen,
  canDelete,
  onDelete,
}: ManualTableProps) {
  const { t } = useTranslation('counseling-manual');

  return (
    // SHCT-606 — fixed layout so a long title/preview stays inside its cell
    // instead of stretching the column and pushing the table past its card.
    // 제목 and 미리보기 are the two unpinned columns; they share the remainder.
    <table className="table table--clickable table--fixed">
      <thead>
        <tr>
          <SortableHeader label={t('list.columns.cid')} columnKey="cid" sortKey={sortKey} sortDir={sortDir} onSort={onSort} style={{ width: 90 }} />
          <SortableHeader label={t('list.columns.title')} columnKey="title" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
          {/* Preview is non-sortable per PRD §4.1. */}
          <th>{t('list.columns.preview')}</th>
          <SortableHeader label={t('list.columns.grade')} columnKey="grade" sortKey={sortKey} sortDir={sortDir} onSort={onSort} style={{ width: 90 }} />
          <SortableHeader label={t('list.columns.category')} columnKey="category" sortKey={sortKey} sortDir={sortDir} onSort={onSort} style={{ width: 260 }} />
          <SortableHeader label={t('list.columns.statusDate')} columnKey="publishedAt" sortKey={sortKey} sortDir={sortDir} onSort={onSort} style={{ width: 150, textAlign: 'center' }} />
          {/* PRD §4.1 — 삭제 is an unconditional column; the ✕ inside is what
              gets disabled per role (PRD §3.7 / §8.3), not the column itself. */}
          <th style={{ width: 60, textAlign: 'center' }}>{t('list.columns.delete')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const categoryPath = [row.area, row.subArea, row.item].map((v) => v || '-').join(' / ');
          return (
            <tr key={row.cid} onClick={() => onRowOpen(row)}>
              <td>{row.cid}</td>
              {/* SHCT-606 — truncate by pixel width too (not just by character
                  count), with the full value on hover. */}
              <td className="cell-ellipsis" title={row.title || undefined}>
                <span className="link-text">{truncate(row.title || t('list.untitled'), TITLE_MAX)}</span>
              </td>
              <td
                className="cell-ellipsis"
                title={row.preview ? decodeHtmlEntities(row.preview) : undefined}
              >
                <span style={{ color: 'var(--muted-foreground)', fontSize: 12 }}>
                  {row.preview ? truncate(decodeHtmlEntities(row.preview), PREVIEW_MAX) : '-'}
                </span>
              </td>
              <td>
                <GradeBadge grade={row.grade} />
              </td>
              <td>{categoryPath}</td>
              <td style={{ textAlign: 'center' }}>
                <ManualStatusBadge manual={row} />
              </td>
              {/* PRD §2.2 #9 — the whole row opens the panel EXCEPT the ✕ cell,
                  so the cell swallows the click even when the ✕ is disabled. */}
              <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                {/* PRD §3.7 / §8.3 — ✕ disabled + tooltip for roles without
                    CONTENT_LIB:DELETE. The tooltip and the not-allowed cursor
                    live on a wrapper <span>: a disabled <button> receives no
                    pointer events, so a `title` on the button itself would
                    never surface on hover (this was the "no tooltip" defect). */}
                <span
                  title={canDelete ? t('list.deleteRow') : t('list.deleteForbidden')}
                  style={{ display: 'inline-block', cursor: canDelete ? 'pointer' : 'not-allowed' }}
                >
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    disabled={!canDelete}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!canDelete) return;
                      onDelete(row);
                    }}
                    aria-label={t('list.deleteRow')}
                    style={{
                      color: 'var(--destructive)',
                      padding: '2px 8px',
                      opacity: canDelete ? 1 : 0.4,
                      cursor: canDelete ? 'pointer' : 'not-allowed',
                      pointerEvents: canDelete ? undefined : 'none',
                    }}
                  >
                    ✕
                  </button>
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
